import type { RefObject } from "react"
import { useEffect, useRef } from "react"

const TYPING_SPEED = 40
const DELETING_SPEED = 20
const PAUSE_AFTER_TYPING = 2000
const PAUSE_AFTER_DELETING = 800

export const useTypingPlaceholder = (ref: RefObject<HTMLElement | null>, placeholders: string[]): void => {
	const indexRef = useRef(0)
	const charRef = useRef(0)
	const isDeletingRef = useRef(false)

	useEffect(() => {
		if (placeholders.length === 0) {
			return
		}

		const setPlaceholder = (text: string) => {
			const el = ref.current
			if (!el) {
				return
			}
			// PInputSearch is a web component – set the attribute directly
			el.setAttribute("placeholder", text)
		}

		const isInputFocused = () => {
			const el = ref.current
			if (!el) {
				return false
			}
			// Check if the shadow DOM input or the host itself is focused
			return el.matches(":focus-within") || el.contains(document.activeElement)
		}

		const tick = (): number => {
			if (isInputFocused()) {
				return 200 // check again later
			}

			const current = placeholders[indexRef.current]
			const isDeleting = isDeletingRef.current

			if (!isDeleting) {
				charRef.current++
				setPlaceholder(current.slice(0, charRef.current))

				if (charRef.current >= current.length) {
					isDeletingRef.current = true
					return PAUSE_AFTER_TYPING
				}
				return TYPING_SPEED
			}

			charRef.current--
			setPlaceholder(current.slice(0, charRef.current))

			if (charRef.current <= 0) {
				isDeletingRef.current = false
				indexRef.current = (indexRef.current + 1) % placeholders.length
				return PAUSE_AFTER_DELETING
			}
			return DELETING_SPEED
		}

		let timeoutId: ReturnType<typeof setTimeout>

		const schedule = () => {
			const delay = tick()
			timeoutId = setTimeout(schedule, delay)
		}

		timeoutId = setTimeout(schedule, PAUSE_AFTER_DELETING)

		return () => clearTimeout(timeoutId)
	}, [ref, placeholders])
}
